import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Check, Code2, Copy, Download, KeyRound, Lock, Monitor, Slack, Smartphone, TerminalSquare, X } from "lucide-react";
import useClosing from "@/lib/useClosing";

// Page « Applications » de Vanta AI (comme celle de Claude) : l'application sur ordinateur
// et téléphone (installation adaptée à l'appareil), Microsoft 365 (Word, Excel, PowerPoint,
// Outlook), Terminal, VS Code, JetBrains et Slack. Les intégrations passent par l'API
// (clé vsk_…) : offres Pro et Max.

function device() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Mac/i.test(ua)) return "mac";
  if (/Win/i.test(ua)) return "windows";
  return "linux";
}
// L'APK et le plugin JetBrains sont construits par GitHub Actions puis déposés sur le site :
// tant qu'un fichier n'y est pas, le lien renverrait la page du site au lieu du fichier.
function useAvailable(url) {
  const [ok, setOk] = useState(null);
  useEffect(() => {
    let live = true;
    fetch(url, { method: "HEAD" })
      .then((r) => live && setOk(r.ok && !(r.headers.get("content-type") || "").includes("text/html")))
      .catch(() => live && setOk(false));
    return () => { live = false; };
  }, [url]);
  return ok;
}

const DEVICE_NAMES = { windows: "Windows", mac: "Mac", linux: "Linux", ios: "iPhone", android: "Android" };

function Tile({ color, children }) {
  return <span className={`w-7 h-7 rounded-md flex items-center justify-center text-[13px] font-bold text-white shrink-0 ${color}`}>{children}</span>;
}

const OFFICE = [
  { id: "excel", name: "Excel", tile: <Tile color="bg-[#107c41]">X</Tile> },
  { id: "powerpoint", name: "PowerPoint", tile: <Tile color="bg-[#c43e1c]">P</Tile> },
  { id: "word", name: "Word", tile: <Tile color="bg-[#185abd]">W</Tile> },
  { id: "outlook", name: "Outlook", tile: <Tile color="bg-[#0a64ad]">O</Tile>, beta: true },
];
const DEV = [
  { id: "terminal", name: "Terminal", tile: <span className="w-7 h-7 flex items-center justify-center text-white/80"><TerminalSquare className="w-5 h-5" /></span> },
  { id: "vscode", name: "VS Code", tile: <span className="w-7 h-7 flex items-center justify-center text-[#3ea6ff]"><Code2 className="w-5 h-5" /></span> },
  { id: "jetbrains", name: "JetBrains", tile: <Tile color="bg-gradient-to-br from-[#ff318c] via-[#fd8624] to-[#087cfa]">JB</Tile> },
  { id: "slack", name: "Slack", tile: <span className="w-7 h-7 flex items-center justify-center text-[#e8a33c]"><Slack className="w-5 h-5" /></span> },
];

function CopyBox({ text, label }) {
  const [done, setDone] = useState(false);
  const copy = () => navigator.clipboard.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }, () => toast.error("Copie impossible"));
  return (
    <div className="relative group">
      {label && <p className="text-[11px] text-white/45 mb-1">{label}</p>}
      <pre className="rounded-lg bg-black/40 border border-white/10 p-3 pr-10 text-xs text-white/85 overflow-x-auto whitespace-pre-wrap break-all">{text}</pre>
      <button type="button" onClick={copy} aria-label="Copier" className="absolute right-2 bottom-2 p-1.5 rounded-md bg-white/10 hover:bg-white/20">
        {done ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="w-6 h-6 rounded-full bg-white/10 text-xs flex items-center justify-center shrink-0 mt-0.5">{n}</span>
      <div className="min-w-0 flex-1 text-sm text-white/75 space-y-2">{children}</div>
    </li>
  );
}

function KeyStep({ n }) {
  return (
    <Step n={n}>
      <p>Crée une clé API (une par appareil, c'est plus sûr).</p>
      <a href="/ai/api" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sky-300 hover:underline"><KeyRound className="w-3.5 h-3.5" />Créer une clé API</a>
    </Step>
  );
}

function Guide({ id, onClose }) {
  const [closing, close] = useClosing(onClose);
  const jbReady = useAvailable("/downloads/vanta-ai-jetbrains.zip");
  const os = device();
  const py = os === "windows" ? "python" : "python3";
  const office = OFFICE.find((o) => o.id === id);
  const title = office ? `Vanta AI dans ${office.name}` : { terminal: "Vanta AI dans le terminal", vscode: "Vanta AI dans VS Code", jetbrains: "Vanta AI dans JetBrains", slack: "Vanta AI dans Slack" }[id];
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [close]);

  return (
    <div className={`vai-overlay fixed inset-0 z-[75] bg-black/70 flex items-center justify-center p-4 ${closing ? "is-closing" : ""}`} onMouseDown={close} role="dialog" aria-modal="true" aria-label={title}>
      <div className="vai-dialog w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#121218] shadow-2xl p-5" onMouseDown={(e) => e.stopPropagation()} data-testid={`vai-guide-${id}`}>
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-semibold flex-1">{title}</h2>
          <button type="button" onClick={close} className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5" aria-label="Fermer"><X className="w-4 h-4" /></button>
        </div>
        <ol className="space-y-4">
          {office && id !== "outlook" && (
            <>
              <KeyStep n={1} />
              <Step n={2}>
                <p>Télécharge le complément Vanta AI (le même fichier pour Word, Excel et PowerPoint).</p>
                <a href="/office/manifest.xml" download="vanta-ai-office.xml" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-black text-sm font-medium"><Download className="w-4 h-4" />Télécharger le complément</a>
              </Step>
              <Step n={3}>
                <p>Dans {office.name} : <b>Accueil › Compléments › Plus de compléments › Mes compléments › Charger mon complément</b>, puis choisis le fichier.</p>
                <p className="text-xs text-white/45">Sur office.com : Insertion › Compléments › Charger mon complément.</p>
              </Step>
              <Step n={4}><p>Ouvre <b>Vanta AI</b> dans Compléments et colle ta clé. Sélectionne du texte ou des cellules, demande, puis « Insérer dans le document ».</p></Step>
            </>
          )}
          {id === "outlook" && (
            <>
              <KeyStep n={1} />
              <Step n={2}>
                <p>Télécharge le complément Vanta AI pour Outlook.</p>
                <a href="/office/outlook-manifest.xml" download="vanta-ai-outlook.xml" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-black text-sm font-medium"><Download className="w-4 h-4" />Télécharger le complément</a>
              </Step>
              <Step n={3}>
                <p>Ouvre <a href="https://aka.ms/olksideload" target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:underline">aka.ms/olksideload</a> (connecté à Outlook) : <b>Mes compléments › Compléments personnalisés › Ajouter à partir d'un fichier</b>.</p>
              </Step>
              <Step n={4}><p>Dans un mail, ouvre <b>Applications › Vanta AI</b> : résume un mail ou rédige une réponse et insère-la.</p></Step>
            </>
          )}
          {id === "terminal" && (
            <>
              <KeyStep n={1} />
              <Step n={2}>
                <p>Télécharge l'outil (il faut Python 3, gratuit sur python.org).</p>
                <a href="/downloads/vanta.py" download="vanta.py" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-black text-sm font-medium"><Download className="w-4 h-4" />Télécharger vanta.py</a>
              </Step>
              <Step n={3}>
                <p>Dans le dossier du fichier, lance :</p>
                <CopyBox text={`${py} vanta.py "Écris un script FiveM qui téléporte le joueur"`} />
                <p className="text-xs text-white/45">Ta clé est demandée la première fois. Sans question : conversation (tape /exit pour quitter).</p>
                {os !== "windows" && <CopyBox label="Optionnel : la commande « vanta » partout" text="sudo install -m 755 vanta.py /usr/local/bin/vanta" />}
              </Step>
            </>
          )}
          {id === "vscode" && (
            <>
              <KeyStep n={1} />
              <Step n={2}>
                <p>Télécharge l'extension Vanta AI (VS Code, Cursor, VSCodium…).</p>
                <a href="/downloads/vanta-ai.vsix" download="vanta-ai.vsix" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-black text-sm font-medium"><Download className="w-4 h-4" />Télécharger l'extension (.vsix)</a>
              </Step>
              <Step n={3}>
                <p>Dans VS Code : <b>Extensions</b> (Ctrl+Maj+X) › <b>⋯</b> en haut › <b>Installer à partir de VSIX…</b>, puis choisis le fichier.</p>
                <CopyBox label="Ou dans un terminal :" text="code --install-extension vanta-ai.vsix" />
              </Step>
              <Step n={4}>
                <p>Clique sur l'icône <b>Vanta AI</b> à gauche et colle ta clé. Sélectionne du code, clic droit › <b>Vanta AI</b> : expliquer, corriger, demander (Ctrl+Alt+V). « Insérer » met le code de la réponse dans ton fichier.</p>
              </Step>
            </>
          )}
          {id === "jetbrains" && (
            <>
              <Step n={1}>
                <p>Télécharge le plugin Vanta AI (IntelliJ IDEA, PyCharm, WebStorm, PhpStorm, Rider… version 2023.3 ou plus récente).</p>
                {jbReady === false
                  ? <p className="text-white/45 text-xs">Le plugin est en cours de préparation : reviens dans quelques minutes.</p>
                  : <a href="/downloads/vanta-ai-jetbrains.zip" download="vanta-ai-jetbrains.zip" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-black text-sm font-medium"><Download className="w-4 h-4" />Télécharger le plugin (.zip)</a>}
              </Step>
              <Step n={2}>
                <p>Dans l'IDE : <b>Réglages › Plugins › ⚙ › Install Plugin from Disk…</b>, choisis le fichier (sans le décompresser), puis redémarre.</p>
              </Step>
              <Step n={3}>
                <p>Ouvre le panneau <b>Vanta AI</b> à droite et connecte-toi avec ton compte. Sélectionne du code, clic droit › <b>Demander à Vanta AI</b> (Ctrl+Alt+V).</p>
              </Step>
            </>
          )}
          {id === "slack" && (
            <>
              <KeyStep n={1} />
              <Step n={2}>
                <p>Ajoute Vanta AI à ton espace Slack.</p>
                <a href="/api/vai/slack/install" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-black text-sm font-medium"><Slack className="w-4 h-4" />Ajouter à Slack</a>
              </Step>
              <Step n={3}>
                <p>Dans Slack, relie ton compte (la commande n'est visible que par toi) :</p>
                <CopyBox text="/vanta connect TA_CLÉ_API" />
              </Step>
              <Step n={4}>
                <p>Pose tes questions dans n'importe quel salon :</p>
                <CopyBox text="/vanta Écris un message d'accueil pour mon serveur" />
              </Step>
            </>
          )}
        </ol>
        <p className="text-xs text-white/35 mt-5">Ne partage jamais ta clé API. Tu peux la supprimer à tout moment dans Clés API.</p>
      </div>
    </div>
  );
}

function Row({ app, locked, onOpen, onUpgrade }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-white/[0.06] last:border-0">
      {app.tile}
      <span className="text-sm flex-1 min-w-0 flex items-center gap-2">{app.name}{app.beta && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-sky-400/40 text-sky-300">Bêta</span>}</span>
      {locked ? (
        <button type="button" onClick={onUpgrade} className="inline-flex items-center gap-1.5 w-28 justify-center py-1.5 rounded-lg text-xs border border-violet-400/30 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20" data-testid={`vai-app-${app.id}-locked`}>
          <Lock className="w-3 h-3" />Pro
        </button>
      ) : (
        <button type="button" onClick={() => onOpen(app.id)} className="inline-flex items-center gap-1.5 w-28 justify-center py-1.5 rounded-lg text-sm bg-white/[0.08] hover:bg-white/[0.14] transition-colors" data-testid={`vai-app-${app.id}`}>
          Installer<ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function Card({ title, text, children, className = "", badge }) {
  return (
    <section className={`rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex flex-col ${className}`}>
      <h2 className="font-semibold flex items-center gap-2">{title}{badge}</h2>
      <p className="text-sm text-white/55 mt-1.5">{text}</p>
      <div className="mt-4 flex-1">{children}</div>
    </section>
  );
}

export default function AppsPage({ me, installed, onInstall, onUpgrade, onBack }) {
  const [guide, setGuide] = useState(null);
  const apkReady = useAvailable("/downloads/vanta-ai.apk");
  const os = device();
  const phone = os === "ios" || os === "android";
  const locked = !me.plan.api;
  const freeBadge = <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 font-normal">Pour tous</span>;
  const proBadge = <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-violet-400/30 bg-violet-500/10 text-violet-200 font-normal">Pro et Max</span>;

  useEffect(() => {
    const slack = new URLSearchParams(window.location.search).get("slack");
    if (slack === "ok") toast.success("Vanta AI est ajouté à Slack : relie ton compte avec /vanta connect");
    else if (slack === "indisponible") toast.error("Slack n'est pas encore activé sur Vanta AI");
    else if (slack === "erreur") toast.error("L'ajout à Slack n'a pas abouti : réessaie");
  }, []);

  return (
    <div className="flex-1 overflow-y-auto" data-testid="vai-apps">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white mb-6"><ArrowLeft className="w-4 h-4" />Retour</button>
        <h1 className="text-3xl font-semibold tracking-tight">Applications</h1>
        <p className="text-white/55 mt-2">Vanta AI sur ton ordinateur, ton téléphone et dans tes outils de travail.</p>

        <div className="grid md:grid-cols-2 gap-4 mt-8">
          <Card title={<><Monitor className="w-4 h-4" />Ordinateur</>} badge={freeBadge} text="L'application Vanta AI dans sa propre fenêtre, avec son icône, sans barre de navigateur.">
            <button type="button" onClick={onInstall} disabled={installed} data-testid="vai-install-desktop"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-white/90 disabled:opacity-60">
              {installed ? <><Check className="w-4 h-4" />Déjà installée</> : <><Download className="w-4 h-4" />Installer pour {phone ? "ordinateur" : DEVICE_NAMES[os]}</>}
            </button>
            <p className="text-xs text-white/40 mt-2">Windows, Mac et Linux (Chrome ou Edge).</p>
          </Card>
          <Card title={<><Smartphone className="w-4 h-4" />Mobile</>} badge={freeBadge} text="Vanta AI sur l'écran d'accueil de ton téléphone, comme une vraie application.">
            <div className="flex items-center gap-3 py-2.5 border-b border-white/[0.06]">
              <span className="text-sm flex-1">Android <span className="text-white/40 text-xs">· application .apk</span></span>
              {apkReady === false
                ? <span className="w-32 py-1.5 rounded-lg text-sm text-center bg-white/[0.05] text-white/45 cursor-default" data-testid="vai-install-android">Bientôt</span>
                : <a href="/downloads/vanta-ai.apk" download="Vanta-AI.apk" className="w-32 py-1.5 rounded-lg text-sm text-center bg-white text-black font-medium hover:bg-white/90" data-testid="vai-install-android">Télécharger</a>}
            </div>
            <div className="flex items-center gap-3 py-2.5">
              <span className="text-sm flex-1">iPhone et iPad</span>
              <button type="button" onClick={onInstall} className="w-32 py-1.5 rounded-lg text-sm bg-white/[0.08] hover:bg-white/[0.14]" data-testid="vai-install-ios">
                {os === "ios" ? "Installer" : "Comment faire"}
              </button>
            </div>
            <p className="text-xs text-white/40 mt-2">
              {apkReady === false ? "Android : l'application .apk arrive très bientôt sur cette page." : "Android : ouvre le fichier téléchargé"}{apkReady !== false && <> et autorise l'installation (« sources inconnues ») si ton téléphone le demande.</>}
              {!phone && " Ou ouvre cette page sur ton téléphone."}
            </p>
          </Card>
          <Card title="Microsoft 365" badge={proBadge} text="Écris, résume et analyse avec Vanta AI à côté de tes documents, tableaux, présentations et mails.">
            {OFFICE.map((app) => <Row key={app.id} app={app} locked={locked} onOpen={setGuide} onUpgrade={onUpgrade} />)}
          </Card>
          <Card title="Développeurs" badge={proBadge} text="Code, corrige et explique avec Vanta AI dans ton terminal, ton éditeur et Slack.">
            {DEV.map((app) => <Row key={app.id} app={app} locked={locked} onOpen={setGuide} onUpgrade={onUpgrade} />)}
          </Card>
        </div>
        {locked && (
          <p className="text-sm text-white/55 mt-6 text-center">
            Microsoft 365, Terminal, VS Code, JetBrains et Slack sont inclus dans les offres Pro et Max.{" "}
            <button type="button" onClick={onUpgrade} className="text-sky-300 hover:underline">Voir les offres</button>
          </p>
        )}
      </div>
      {guide && <Guide id={guide} onClose={() => setGuide(null)} />}
    </div>
  );
}
